import { ReactNode, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAuthModal } from "@/contexts/AuthModalContext";
import { Spinner } from "@/components/flowly/infra/Spinner/Spinner";
import { VerifyEmail } from "../VerifyEmail/VerifyEmail";

interface ProtectedRouteProps {
  children: ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { openAuthModal } = useAuthModal();
  const location = useLocation();

  useEffect(() => {
    // Open auth modal when redirecting unauthenticated user
    if (!loading && !user) {
      openAuthModal("login");
    }
  }, [loading, user, openAuthModal]);

  // Show loading spinner while auth state is being determined
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner size="md" />
      </div>
    );
  }

  // Redirect to home if not authenticated
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Safety net: show verification page if email not confirmed
  if (!user.email_confirmed_at) {
    return <VerifyEmail />;
  }

  // Render protected content
  return <>{children}</>;
}
